/**
 * Адреса раздела /shared/ (ingdg_com#182).
 *
 * Адрес открытого файла или папки на сайте:
 *
 *   /shared/<папка>/<подпапка>/<имя>--<uuid>
 *
 * Слаги в адресе для человека и поисковика: по ним видно, что это за
 * файл и где он лежит. Источник истины один: uuid в последнем сегменте.
 * Переименование файла или папки не ломает разосланные ссылки, а адрес
 * с устаревшим слагом уводится редиректом на актуальный, чтобы у одной
 * страницы был один адрес.
 *
 * Разделитель `--` между слагом и идентификатором, а не `-`: uuid сам
 * содержит дефисы, и одинарный разделитель не давал бы отличить конец
 * слага от начала идентификатора, когда слаг пуст или кончается цифрой.
 *
 * Путь берётся от открытой папки вниз, а не от корня хранилища: имена
 * приватных папок над ней человек не открывал (см. `path` в ответе API).
 *
 * Модуль без зависимостей от Next: его читают и страница, и middleware,
 * и окно «Настроить» в кабинете, и тесты.
 */

/** Разделитель слага и идентификатора в последнем сегменте. */
export const SHARED_ID_SEPARATOR = '--';

/** Префикс раздела на сайте, без локали. */
export const SHARED_PREFIX = '/shared';

/** UUID в нижнем регистре: единственная форма, которую принимает API. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Слаг из имени файла или папки.
 *
 * Кириллица транслитерируется: адрес с процентами (`%D0%A3%D1%80`)
 * в мессенджере и в выдаче нечитаем, а транслит читается. Точка в
 * расширении сохраняется как часть имени (`урок.pdf` → `urok-pdf`):
 * она не несёт смысла для адреса, а расширение помогает понять, что
 * за файл. Пустой результат допустим: адрес соберётся из одного
 * идентификатора.
 */
export function sharedSlug(name: string): string {
  const translit = transliterate(name.toLowerCase());
  return translit
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '');
}

/**
 * Одна папка или файл в адресе.
 *
 * Только имя: слаг у папки без идентификатора, потому что папка в
 * середине пути служит ориентиром, а не адресом. Открывается то, чей
 * идентификатор стоит в конце.
 */
export interface SharedNode {
  id: string;
  name: string;
}

/**
 * Путь раздела для узла и открытых папок над ним.
 *
 * @param path папки от внешней к ближайшей, как их отдаёт API
 * @param node сам файл или папка
 */
export function sharedPath(path: readonly SharedNode[], node: SharedNode): string {
  const folders = path.map((p) => sharedSlug(p.name)).filter((s) => s !== '');
  const slug = sharedSlug(node.name);
  const last = slug === '' ? node.id : `${slug}${SHARED_ID_SEPARATOR}${node.id}`;
  return [SHARED_PREFIX, ...folders, last].join('/');
}

/**
 * Идентификатор из пути раздела.
 *
 * Берётся из последнего сегмента после разделителя; сегмент из одного
 * uuid тоже принимается. Всё остальное в пути игнорируется: слаги для
 * людей, а не для машины. `null`, если идентификатора нет или он не
 * похож на uuid: такой адрес не может вести ни к какому файлу, и
 * спрашивать API незачем.
 */
export function sharedIdFromPath(pathname: string): string | null {
  const clean = pathname.split('?')[0]?.split('#')[0] ?? '';
  const segments = clean.split('/').filter((s) => s !== '');
  const last = segments[segments.length - 1];
  if (last === undefined) return null;

  let decoded: string;
  try {
    decoded = decodeURIComponent(last);
  } catch {
    return null;
  }

  const at = decoded.lastIndexOf(SHARED_ID_SEPARATOR);
  const candidate = (
    at === -1 ? decoded : decoded.slice(at + SHARED_ID_SEPARATOR.length)
  ).toLowerCase();
  return UUID_RE.test(candidate) ? candidate : null;
}

/** Путь относится к разделу /shared (с локалью в начале или без). */
export function isSharedPath(pathname: string): boolean {
  return /^\/(?:[a-z]{2}\/)?shared(?:\/|$)/.test(pathname);
}

/**
 * Транслитерация по ГОСТ 7.79-2000 (схема Б) в упрощении: без
 * диакритики, так адрес остаётся в ASCII. Остальные буквы (латиница,
 * цифры) проходят как есть; неизвестные знаки остаются и превращаются
 * в дефис на следующем шаге.
 */
const CYRILLIC: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'yo',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'j',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'c',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
  // Сербская кириллица: домен .tech обслуживает и сербский язык.
  ђ: 'dj',
  ј: 'j',
  љ: 'lj',
  њ: 'nj',
  ћ: 'c',
  џ: 'dz',
};

function transliterate(text: string): string {
  let out = '';
  for (const ch of text) {
    out += CYRILLIC[ch] ?? ch;
  }
  return out;
}
